import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Tooltip,
  Typography,
  Avatar
} from '@material-ui/core';
import SendIcon from '@mui/icons-material/Send';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import MarkEmailReadRoundedIcon from '@mui/icons-material/MarkEmailReadRounded';
import ScheduleSendRoundedIcon from '@mui/icons-material/ScheduleSendRounded';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMoreRounded';
import toast from 'react-hot-toast';
import * as moment from 'moment';
import { formateurSurveyApi } from '../../../api/formateurSurveyApi';
import StarRating from '../questionnaire/responses/StarRating';
import { answerValues, renderAnswer, scoreColor } from '../questionnaire/responses/constants';
import useMounted from '../../../hooks/useMounted';
import { alpha } from '@mui/material/styles';
import { getTone, toneAlpha, grey } from '../../../theme/tones';

/**
 * Questionnaire de satisfaction du formateur, dans l'onglet « Suivi des réponses ».
 *
 * Deux modèles seulement, communs à toutes les formations : un pour le
 * présentiel, un pour la classe virtuelle. Le back choisit le bon d'après le
 * format de la formation, et refuse l'envoi si la session n'a pas de formateur
 * ou si sa fiche intervenant n'a pas d'e-mail.
 */
/**
 * Tuile d'information du bandeau : icône teintée, libellé, valeur, précision.
 * Les trois tuiles (destinataire, e-mail, réponse) se partagent la largeur.
 */
const InfoTile = ({ icon, label, value, hint, tint }) => (
  <Box
    sx={{
      flex: '1 1 200px',
      minWidth: 0,
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.75,
      p: 2.25,
      borderRadius: '16px',
      border: `1px solid ${grey(200)}`,
      backgroundColor: 'background.paper',
      boxShadow: (theme) => `0 1px 2px ${alpha(theme.palette.text.primary, 0.04)}`
    }}
  >
    <Box
      sx={{
        width: 38,
        height: 38,
        flexShrink: 0,
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: tint.bg,
        color: tint.fg
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          display: 'block',
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.07em',
          textTransform: 'uppercase',
          color: 'text.disabled'
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ mt: 0.25, fontSize: 15, fontWeight: 700, lineHeight: 1.35, color: tint.value, overflowWrap: 'anywhere' }}>
        {value}
      </Typography>
      {hint && (
        <Typography sx={{ mt: 0.25, fontSize: 12.5, color: 'text.secondary', overflowWrap: 'anywhere' }}>
          {hint}
        </Typography>
      )}
    </Box>
  </Box>
);

InfoTile.propTypes = {
  icon: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  hint: PropTypes.string,
  tint: PropTypes.shape({
    bg: PropTypes.string.isRequired,
    fg: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired
  }).isRequired
};

InfoTile.defaultProps = {
  hint: null
};

const parseAnswers = (raw) => {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return {};
  }
};

const parseOptions = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

/**
 * Le formulaire tel qu'il a été rempli : les questions du modèle dans l'ordre,
 * chacune avec sa réponse. Les questions restées vides sont conservées mais
 * estompées — savoir ce à quoi le formateur n'a pas répondu fait partie de
 * l'information.
 */
const SubmissionAnswers = ({ submission, questions }) => {
  const answers = parseAnswers(submission.answers);
  const lines = questions
    .filter((q) => String(q.questionnaire_id) === String(submission.questionnaire_id))
    .map((q) => {
      const options = parseOptions(q.options);
      const values = answerValues(answers[q.id]);
      let weight = null;
      if (options.length && values.length) {
        const match = options.find((o) => String(o && o.text !== undefined ? o.text : o).trim() === values[0]);
        if (match && match.weight !== undefined && match.weight !== null) weight = Number(match.weight);
      }
      return {
        id: q.id,
        text: q.text,
        isChoice: options.length > 0,
        answered: values.length > 0,
        answer: renderAnswer(answers[q.id]),
        weight
      };
    });

  if (!lines.length) {
    return (
      <Typography sx={{ fontSize: '0.8rem', color: 'text.disabled', fontWeight: 600, py: 1 }}>
        Les questions de ce modèle sont introuvables.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1.5 }}>
      {lines.map((line, index) => (
        <Box
          key={line.id}
          sx={{
            p: 1.75,
            borderRadius: '12px',
            border: `1px solid ${grey(100)}`,
            backgroundColor: 'background.paper',
            opacity: line.answered ? 1 : 0.6
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography sx={{ fontSize: '0.66rem', fontWeight: 900, color: grey(300), minWidth: 24 }}>
              {String(index + 1).padStart(2, '0')}
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: '0.83rem', color: 'text.primary', flexGrow: 1 }}>
              {line.text}
            </Typography>
            {line.weight !== null && (
              <Tooltip title="Poids de l'option choisie dans la note globale">
                <Box
                  sx={{
                    px: 1, py: 0.2, borderRadius: '8px', flexShrink: 0,
                    backgroundColor: `${scoreColor(line.weight)}1A`,
                    color: scoreColor(line.weight),
                    fontWeight: 900, fontSize: '0.66rem'
                  }}
                >
                  {line.weight}
                </Box>
              </Tooltip>
            )}
          </Box>
          <Typography
            sx={{
              mt: 0.6,
              ml: 4,
              fontSize: '0.83rem',
              fontWeight: line.isChoice ? 700 : 500,
              color: line.answered ? grey(700) : grey(400),
              fontStyle: line.isChoice || !line.answered ? 'normal' : 'italic',
              borderLeft: line.isChoice ? 'none' : `3px solid ${toneAlpha('indigo', 0.25)}`,
              pl: line.isChoice ? 0 : 1.5,
              whiteSpace: 'pre-wrap'
            }}
          >
            {line.answered ? line.answer : 'Sans réponse'}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

SubmissionAnswers.propTypes = {
  submission: PropTypes.object.isRequired,
  questions: PropTypes.array.isRequired
};

const SessionFormateurSurvey = ({ sessionId }) => {
  const [plan, setPlan] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const mounted = useMounted();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const [planRes, subsRes] = await Promise.all([
        formateurSurveyApi.getPlan(sessionId, token),
        formateurSurveyApi.getSubmissions(sessionId, token)
      ]);
      if (mounted.current) {
        setPlan(planRes);
        const rows = subsRes && Array.isArray(subsRes.data) ? subsRes.data : [];
        setSubmissions(rows);
        setQuestions(subsRes && Array.isArray(subsRes.questions) ? subsRes.questions : []);
        // Toujours replié au chargement : la ligne du formateur et sa note
        // suffisent au coup d'œil, le détail des 37 questions ne se déroule
        // que si on le demande par le chevron.
        setOpenId(null);
      }
    } catch (err) {
      console.error(err);
      if (mounted.current) {
        setPlan({ available: false, message: err.message || 'Impossible de charger le questionnaire formateur.' });
      }
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [sessionId, mounted]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await formateurSurveyApi.send(sessionId, '', localStorage.getItem('accessToken'));
      if (res && res.status === 200) {
        toast.success(res.message || 'Questionnaire envoyé au formateur !');
        load();
      } else {
        toast.error((res && res.message) || "L'envoi a échoué.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Une erreur est survenue lors de l'envoi !");
    }
    setSending(false);
  };

  if (loading) {
    return (
      <Card sx={{ borderRadius: '20px', border: (theme) => `1px solid ${theme.palette.divider}`, p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={26} />
      </Card>
    );
  }

  const available = plan && plan.available;
  const answered = available && plan.answered_at;
  // Date du dernier envoi réussi. Les envois antérieurs au suivi n'en ont pas :
  // une réponse du formateur prouve à elle seule que l'e-mail est bien parti.
  const sentAt = available ? plan.sent_at : null;
  const sent = Boolean(sentAt) || Boolean(answered);
  // Note de la réponse la plus récente : `null` tant qu'aucune question à choix
  // n'a été notée (réponse antérieure au calcul, ou entièrement libre).
  const note = submissions.length && submissions[0].average !== null && submissions[0].average !== undefined
    ? submissions[0].average
    : null;

  return (
    <Card
      sx={{
        borderRadius: '20px',
        overflow: 'hidden',
        border: (theme) => `1px solid ${theme.palette.divider}`,
        boxShadow: (theme) => `0 4px 6px -1px ${alpha(theme.palette.common.black, 0.1)}, 0 2px 4px -1px ${alpha(theme.palette.common.black, 0.06)}`,
        background: (theme) => `linear-gradient(135deg, ${theme.palette.common.white} 0%, ${theme.palette.background.default} 100%)`
      }}
    >
      <Box
        sx={{
          p: 3,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              width: 44,
              height: 44,
              background: (theme) => `linear-gradient(135deg, ${theme.palette.tones.indigo.base} 0%, ${theme.palette.tones.violet.base} 100%)`,
              boxShadow: (theme) => `0 4px 10px -2px ${alpha(theme.palette.tones.indigo.base, 0.4)}`
            }}
          >
            <PersonOutlineIcon sx={{ color: grey(0) }} />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Satisfaction du formateur
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {available
                ? `${plan.questionnaire.name} — ${plan.questionnaire.scope}`
                : 'Questionnaire adressé au formateur de la session'}
            </Typography>
          </Box>
        </Box>

        {available && (
          <Button
            variant="outlined"
            disabled={sending}
            onClick={handleSend}
            startIcon={sending ? <CircularProgress size={14} /> : <SendIcon fontSize="small" />}
          >
            {sent ? 'Renvoyer' : 'Envoyer au formateur'}
          </Button>
        )}
      </Box>

      <Box sx={{ p: 3 }}>
        {!available && (
          <Alert severity="info">{plan && plan.message}</Alert>
        )}

        {available && (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <InfoTile
                icon={<PersonOutlineIcon fontSize="small" />}
                label="Destinataire"
                value={plan.recipient.name}
                hint={plan.recipient.email}
                tint={{ bg: getTone('indigo').bg, fg: getTone('indigo').base, value: grey(800) }}
              />
              <InfoTile
                icon={sent ? <MarkEmailReadRoundedIcon fontSize="small" /> : <ScheduleSendRoundedIcon fontSize="small" />}
                label="E-mail"
                value={sent ? 'Envoyé' : 'Pas encore envoyé'}
                hint={
                  // eslint-disable-next-line no-nested-ternary
                  sent
                    ? (sentAt ? `le ${moment(sentAt).format('DD/MM/YYYY [à] HH:mm')}` : "date d'envoi non enregistrée")
                    : "Le questionnaire n'est pas encore parti"
                }
                tint={
                  sent
                    ? { bg: getTone('indigo').bg, fg: getTone('indigo').base, value: getTone('indigo').color }
                    : { bg: getTone('orange').bg, fg: getTone('orange').base, value: getTone('orange').deep }
                }
              />
              <InfoTile
                icon={answered ? <CheckCircleRoundedIcon fontSize="small" /> : <HourglassEmptyRoundedIcon fontSize="small" />}
                label="Réponse"
                value={answered ? 'Répondu' : 'En attente'}
                hint={
                  answered
                    ? `le ${moment(plan.answered_at).format('DD/MM/YYYY [à] HH:mm')}`
                    : "Le formateur n'a pas encore répondu"
                }
                tint={
                  answered
                    ? { bg: getTone('emerald').bgStrong, fg: getTone('emerald').color, value: getTone('emerald').deep }
                    : { bg: grey(100), fg: grey(400), value: getTone('neutral').color }
                }
              />
              {note !== null && (
                <InfoTile
                  icon={<CheckCircleRoundedIcon fontSize="small" />}
                  label="Satisfaction"
                  value={`${note}%`}
                  hint="Moyenne des questions à choix"
                  tint={{ bg: `${scoreColor(note)}1A`, fg: scoreColor(note), value: scoreColor(note) }}
                />
              )}
            </Box>

            {submissions.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                {submissions.map((s) => {
                  const open = openId === s.id;
                  return (
                    <Box key={s.id} sx={{ mb: 1.5 }}>
                      <Box
                        onClick={() => setOpenId(open ? null : s.id)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          p: 1.5,
                          borderRadius: '12px',
                          cursor: 'pointer',
                          border: `1px solid ${grey(200)}`,
                          backgroundColor: 'background.paper',
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                      >
                        <CheckCircleRoundedIcon sx={{ color: 'success.main', fontSize: 18 }} />
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: 'text.primary' }}>
                            {s.formateur}
                          </Typography>
                          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            {s.questionnaire_name}
                            {' · '}
                            {moment(s.created_at).format('DD/MM/YYYY HH:mm')}
                          </Typography>
                        </Box>
                        {s.average !== null && s.average !== undefined && (
                          <>
                            <Chip
                              label={`${s.average}%`}
                              size="small"
                              sx={{
                                backgroundColor: `${scoreColor(s.average)}1A`,
                                color: scoreColor(s.average),
                                fontWeight: 900,
                                fontSize: '0.7rem',
                                height: 22
                              }}
                            />
                            <StarRating percent={s.average} size="0.72rem" color={scoreColor(s.average)} iconSize="0.9rem" />
                          </>
                        )}
                        <ExpandMoreIcon
                          sx={{
                            color: 'text.disabled',
                            transition: 'transform .2s ease',
                            transform: open ? 'rotate(180deg)' : 'none'
                          }}
                        />
                      </Box>

                      <Collapse in={open} unmountOnExit>
                        <SubmissionAnswers submission={s} questions={questions} />
                      </Collapse>
                    </Box>
                  );
                })}
              </>
            )}
          </>
        )}
      </Box>
    </Card>
  );
};

SessionFormateurSurvey.propTypes = {
  sessionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};

export default SessionFormateurSurvey;
